import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, ClipboardList, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useTestPlans, useCreateTestPlan, useUpdateTestPlan, useDeleteTestPlan } from '../api/useTestPlans'
import { useProjects } from '@/features/projects/api/useProjects'
import type { TestPlan, CreateTestPlanRequest, UpdateTestPlanRequest } from '@/types/testplan.types'
import { logger } from '@/lib/utils/logger'
import { formatDate } from '@/lib/utils'

export default function TestPlansPage() {
  const navigate = useNavigate()
  const { data: testplans, isLoading, error } = useTestPlans()
  const { data: projects } = useProjects()
  const createMutation = useCreateTestPlan()
  const updateMutation = useUpdateTestPlan()
  const deleteMutation = useDeleteTestPlan()

  const [filterProjectId, setFilterProjectId] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingPlan, setEditingPlan] = useState<TestPlan | null>(null)
  const [formName, setFormName] = useState('')
  const [formProjectId, setFormProjectId] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [formIsPublic, setFormIsPublic] = useState(true)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [planToDelete, setPlanToDelete] = useState<{ id: number; name: string } | null>(null)

  const filteredPlans = (testplans || []).filter((tp) => {
    if (filterProjectId !== 'all' && tp.test_project_id.toString() !== filterProjectId) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return tp.name.toLowerCase().includes(q) || tp.notes?.toLowerCase().includes(q)
    }
    return true
  })

  const handleCreate = () => {
    setDialogMode('create')
    setEditingPlan(null)
    setFormName('')
    setFormProjectId('')
    setFormNotes('')
    setFormIsPublic(true)
    setDialogOpen(true)
  }

  const handleEdit = (plan: TestPlan) => {
    setDialogMode('edit')
    setEditingPlan(plan)
    setFormName(plan.name)
    setFormProjectId(plan.test_project_id.toString())
    setFormNotes(plan.notes || '')
    setFormIsPublic(plan.is_public)
    setDialogOpen(true)
  }

  // openapi: POST /api/testplans → 201
  const handleSubmit = async () => {
    if (!formName.trim()) {
      toast.error('Test plan name is required')
      return
    }
    try {
      if (dialogMode === 'create') {
        if (!formProjectId) {
          toast.error('Select a project')
          return
        }
        const req: CreateTestPlanRequest = {
          name: formName.trim(),
          test_project_id: parseInt(formProjectId, 10),
          notes: formNotes.trim() || undefined,
          is_public: formIsPublic,
        }
        await createMutation.mutateAsync(req)
        toast.success(`Test plan "${formName}" created`)
      } else if (editingPlan) {
        // openapi: PUT /api/testplans/{testplan_id}
        const req: UpdateTestPlanRequest = {
          name: formName.trim(),
          notes: formNotes.trim() || undefined,
          is_public: formIsPublic,
        }
        await updateMutation.mutateAsync({ id: editingPlan.id, data: req })
        toast.success(`Test plan "${formName}" updated`)
      }
      setDialogOpen(false)
    } catch (err) {
      logger.error('Test plan save failed', { error: err })
      toast.error('Failed to save test plan')
    }
  }

  const handleDeleteClick = (id: number, name: string) => {
    setPlanToDelete({ id, name })
    setDeleteConfirmOpen(true)
  }

  // openapi: DELETE /api/testplans/{testplan_id} → 204
  const handleDeleteConfirm = async () => {
    if (!planToDelete) return
    try {
      await deleteMutation.mutateAsync(planToDelete.id)
      toast.success(`"${planToDelete.name}" deleted`)
    } catch (err) {
      logger.error('Delete test plan failed', { error: err })
      toast.error('Failed to delete test plan')
    } finally {
      setDeleteConfirmOpen(false)
      setPlanToDelete(null)
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">
            {error instanceof Error ? error.message : 'Failed to load test plans'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Test Plans</CardTitle>
              <CardDescription>
                {filteredPlans.length} test plan{filteredPlans.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" /> New Test Plan
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <Select value={filterProjectId} onValueChange={setFilterProjectId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.prefix ? `[${p.prefix}] ` : ''}{p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search test plans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery ? 'No test plans match your search.' : 'No test plans found.'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Builds</TableHead>
                    <TableHead>Test Cases</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlans.map((tp) => (
                    <TableRow
                      key={tp.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/tests/test-plans/${tp.id}`)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                          {tp.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tp.project_name || `Project #${tp.test_project_id}`}
                      </TableCell>
                      <TableCell>{tp.build_count ?? 0}</TableCell>
                      <TableCell>{tp.testcase_count ?? 0}</TableCell>
                      <TableCell>
                        <Badge variant={tp.active ? 'success' : 'default'}>
                          {tp.active ? (tp.is_open ? 'Open' : 'Closed') : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(tp.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(tp)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(tp.id, tp.name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog — POST /api/testplans or PUT /api/testplans/{id} */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Create Test Plan' : 'Edit Test Plan'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'create' ? 'Create a new test plan for organizing test execution.' : 'Update test plan details.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="plan-name">Name</Label>
              <Input id="plan-name" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Test plan name" />
            </div>
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={formProjectId} onValueChange={setFormProjectId}>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects?.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        {p.prefix ? `[${p.prefix}] ` : ''}{p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="plan-notes">Notes</Label>
              <Textarea id="plan-notes" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={3} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="plan-public" checked={formIsPublic} onCheckedChange={(c) => setFormIsPublic(!!c)} />
              <Label htmlFor="plan-public">Public (visible to all team members)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
              ) : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Test Plan"
        description={`Are you sure you want to delete "${planToDelete?.name}"? This will remove all test case assignments.`}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  )
}
